/**
 * Settings, Problem checks: when Orgus raises a problem on its own, for every project (spec,
 * "Checks Orgus runs on every project"). One form; validated on save; errors under their field.
 * Each check reads as its values at rest and opens into its fields on click; an error opens it.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { DEFAULT_NATIVE_CHECK_SETTINGS, NATIVE_CHECK_LABEL } from '@orgus/protocol';
import type { NativeCheckSettings } from '@orgus/protocol';
import { Button, Checkbox, Disclosure, ErrorState, Input, Panel, SkeletonRows, toUiError, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { saveCheckSettings, useProjectProtocol } from './api.ts';
import { validateCheckSettings } from './model.ts';
import type { CheckKey, SettingsErrors } from './model.ts';

type Key = CheckKey;
/** The numbers as typed, and the switches. */
type Draft = Record<Key, string> & { alwaysConfirmActions: boolean; autoFix: boolean; stopIdleDevServers: boolean };

const GROUPS: { title: string; fields: { key: Key; label: string; unit: 'min' | '%' | 'tries' }[]; summary?: (d: Draft) => string }[] = [
  {
    title: NATIVE_CHECK_LABEL['session-waiting'],
    fields: [
      { key: 'sessionWaitingWarnMin', label: 'Warning after', unit: 'min' },
      { key: 'sessionWaitingCriticalMin', label: 'Critical after', unit: 'min' },
    ],
  },
  {
    title: NATIVE_CHECK_LABEL['workflow-stalled'],
    fields: [
      { key: 'workflowStalledWarnMin', label: 'Warning after', unit: 'min' },
      { key: 'workflowStalledCriticalMin', label: 'Critical after', unit: 'min' },
    ],
  },
  {
    title: NATIVE_CHECK_LABEL['worktree-orphaned'],
    fields: [{ key: 'worktreeOrphanedWarnMin', label: 'Warning after', unit: 'min' }],
  },
  {
    title: NATIVE_CHECK_LABEL['cpu-saturated'],
    fields: [
      { key: 'cpuWarnPct', label: 'Warning at', unit: '%' },
      { key: 'cpuWarnMin', label: 'Warning for', unit: 'min' },
      { key: 'cpuCriticalPct', label: 'Critical at', unit: '%' },
      { key: 'cpuCriticalMin', label: 'Critical for', unit: 'min' },
    ],
    summary: (d) => `Warning at ${d.cpuWarnPct || '-'}% for ${d.cpuWarnMin || '-'} min, critical at ${d.cpuCriticalPct || '-'}% for ${d.cpuCriticalMin || '-'} min`,
  },
];

const FIX_FIELDS: { key: Key; label: string; unit: 'min' | 'tries' }[] = [
  { key: 'autoFixAfterMin', label: 'Start a fix after', unit: 'min' },
  { key: 'autoFixMaxAttempts', label: 'Try at most', unit: 'tries' },
];

/** "Warning after 15 min, critical after 60 min": a group's values as one line. */
function summaryOf(fields: { key: Key; label: string; unit: string }[], draft: Draft): string {
  return fields
    .map((f, i) => {
      const label = i === 0 ? f.label : f.label.charAt(0).toLowerCase() + f.label.slice(1);
      const v = draft[f.key].trim() || '-';
      return f.unit === '%' ? `${label} ${v}%` : `${label} ${v} ${f.unit}`;
    })
    .join(', ');
}

const KEYS_ALL: Key[] = [...GROUPS.flatMap((g) => g.fields.map((f) => f.key)), ...FIX_FIELDS.map((f) => f.key)];
const toDraft = (s: NativeCheckSettings): Draft => ({
  ...(Object.fromEntries(KEYS_ALL.map((k) => [k, String(s[k] ?? DEFAULT_NATIVE_CHECK_SETTINGS[k])])) as Record<Key, string>),
  alwaysConfirmActions: !!s.alwaysConfirmActions,
  autoFix: !!s.autoFix,
  stopIdleDevServers: !!s.stopIdleDevServers,
});
const fromDraft = (d: Draft): NativeCheckSettings => ({
  ...(Object.fromEntries(KEYS_ALL.map((k) => [k, d[k].trim() === '' ? Number.NaN : Number(d[k])])) as Record<Key, number>),
  alwaysConfirmActions: d.alwaysConfirmActions,
  autoFix: d.autoFix,
  stopIdleDevServers: d.stopIdleDevServers,
});

export function CheckSettings() {
  const { state, phase, error, retry } = useProjectProtocol();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<SettingsErrors>({});
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<UiError | null>(null);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const saved = state?.settings;
  useEffect(() => {
    if (!focusKey) return;
    document.getElementById(`check-${focusKey}`)?.focus();
    setFocusKey(null);
  }, [focusKey]);
  const setGroup = (id: string, on: boolean) =>
    setOpen((o) => {
      const n = new Set(o);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });

  useEffect(() => {
    if (saved && !draft) setDraft(toDraft(saved));
  }, [saved, draft]);

  if (phase === 'loading' || (phase === 'ready' && !draft)) return <SkeletonRows rows={5} />;
  if (phase === 'error' || !draft) return <ErrorState error={error} onRetry={retry} />;

  const dirty = !!saved && (KEYS_ALL.some((k) => String(saved[k] ?? DEFAULT_NATIVE_CHECK_SETTINGS[k]) !== draft[k]) || !!saved.alwaysConfirmActions !== draft.alwaysConfirmActions || !!saved.autoFix !== draft.autoFix || !!saved.stopIdleDevServers !== draft.stopIdleDevServers);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const next = fromDraft(draft);
    const errs = validateCheckSettings(next);
    setErrors(errs);
    setSaveError(null);
    const first = Object.keys(errs)[0];
    if (first) {
      // Open every check with an error, then focus the first field.
      const bad = new Set(Object.keys(errs));
      setOpen((o) => new Set([...o, ...GROUPS.filter((g) => g.fields.some((f) => bad.has(f.key))).map((g) => g.title), ...(FIX_FIELDS.some((f) => bad.has(f.key)) ? ['fixes'] : [])]));
      setFocusKey(first);
      return;
    }
    setBusy(true);
    try {
      const s = await saveCheckSettings(next);
      setDraft(toDraft(s));
      toast.success('Settings saved');
    } catch (err) {
      setSaveError(toUiError(err, { what: 'The settings were not saved.' }));
    } finally {
      setBusy(false);
    }
  };

  const numberField = (f: { key: Key; label: string; unit: string }, disabled = false) => (
    <Input
      key={f.key}
      id={`check-${f.key}`}
      label={f.label}
      type="number"
      inputMode="numeric"
      min={1}
      step={1}
      disabled={disabled}
      value={draft[f.key]}
      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
      onBlur={() => {
        const errs = validateCheckSettings(fromDraft(draft));
        setErrors((prev) => ({ ...prev, [f.key]: errs[f.key] }));
      }}
      trailing={<span className="text-caption text-fg-subtle">{f.unit}</span>}
      error={errors[f.key]}
    />
  );

  return (
    <form className="flex max-w-(--layout-width-form) flex-col gap-6" onSubmit={(e) => void submit(e)} noValidate>
      <Panel title="Checks" sub={`${GROUPS.length + 2}`} bodyClassName="flex flex-col">
        {GROUPS.map((g) => (
          <Disclosure key={g.title} title={g.title} summary={g.summary ? g.summary(draft) : summaryOf(g.fields, draft)} open={open.has(g.title)} onOpenChange={(v) => setGroup(g.title, v)}>
            <fieldset className="grid gap-4 sm:grid-cols-2">
              <legend className="sr-only">{g.title}</legend>
              {g.fields.map((f) => numberField(f))}
            </fieldset>
          </Disclosure>
        ))}
        <Disclosure
          title="Declared actions"
          summary={draft.alwaysConfirmActions ? 'Confirm every one' : 'As each project declares'}
          open={open.has('declared')}
          onOpenChange={(v) => setGroup('declared', v)}
        >
          <Checkbox
            label="Confirm every declared action"
            hint="Also the ones a project marks confirm: false."
            checked={draft.alwaysConfirmActions}
            onCheckedChange={(v) => setDraft({ ...draft, alwaysConfirmActions: v })}
          />
        </Disclosure>
        <Disclosure
          title="Fixes Orgus starts"
          summary={draft.autoFix ? `On, ${summaryOf(FIX_FIELDS, draft).charAt(0).toLowerCase()}${summaryOf(FIX_FIELDS, draft).slice(1)}` : 'Off'}
          open={open.has('fixes')}
          onOpenChange={(v) => setGroup('fixes', v)}
        >
          <fieldset className="flex flex-col gap-4">
            <legend className="sr-only">Fixes Orgus starts</legend>
            <Checkbox
              label="Let Orgus fix project problems on its own"
              hint="A problem that stays open gets a fix session. After the last try, Orgus asks you."
              checked={draft.autoFix}
              onCheckedChange={(v) => setDraft({ ...draft, autoFix: v })}
            />
            <div className="grid gap-4 sm:grid-cols-2">{FIX_FIELDS.map((f) => numberField(f, !draft.autoFix))}</div>
          </fieldset>
        </Disclosure>
        <Disclosure title="Dev servers" summary={draft.stopIdleDevServers ? 'Stopped when their session ends' : 'Shown in Watch'} open={open.has('dev-servers')} onOpenChange={(v) => setGroup('dev-servers', v)}>
          <Checkbox
            label="Stop dev servers of ended sessions"
            hint="Never one whose session still runs, or one Orgus cannot trace to a session."
            checked={draft.stopIdleDevServers}
            onCheckedChange={(v) => setDraft({ ...draft, stopIdleDevServers: v })}
          />
        </Disclosure>
      </Panel>
      {saveError && <ErrorState variant="inline" error={saveError} />}
      <div className="flex items-center gap-2">
        <Button type="submit" variant="primary" loading={busy} disabled={!dirty}>
          Save settings
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          onClick={() => {
            setDraft({ ...toDraft(DEFAULT_NATIVE_CHECK_SETTINGS), alwaysConfirmActions: draft.alwaysConfirmActions, autoFix: draft.autoFix, stopIdleDevServers: draft.stopIdleDevServers });
            setErrors({});
          }}
        >
          Use defaults
        </Button>
        {dirty && <span className="text-caption text-fg-subtle">Unsaved changes</span>}
      </div>
    </form>
  );
}
